import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";
import { FocusShell } from "@/components/layout/FocusShell";
import { Button, Notice, Panel } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { getBriefPreferences, updateBriefPreferences } from "@/lib/brief";
import { SEO } from "@/lib/seo";

export default function BriefPreferences() {
  const { token = "" } = useParams<{ token: string }>();
  const { city } = useCity();
  const [practiceOwnerUpdates, setPracticeOwnerUpdates] = useState(false);
  const lastSavedValue = useRef(false);
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const preferences = useQuery({
    queryKey: ["brief-preferences", city.slug, token],
    queryFn: () => getBriefPreferences(city.slug, token),
    enabled: Boolean(token),
    retry: false,
  });

  useEffect(() => {
    if (preferences.data) {
      setPracticeOwnerUpdates(preferences.data.practiceOwnerUpdates);
      lastSavedValue.current = preferences.data.practiceOwnerUpdates;
    }
  }, [preferences.data]);

  if (!token) return <Navigate to="/brief" replace />;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("submitting");
    try {
      await updateBriefPreferences(city.slug, token, { practiceOwnerUpdates });
      if (practiceOwnerUpdates && !lastSavedValue.current) track("owner_updates_opt_in");
      lastSavedValue.current = practiceOwnerUpdates;
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <FocusShell>
      <SEO
        title="Canberra Referral Brief preferences · PulseBoard"
        description="Update your Canberra Referral Brief preferences."
        path={`/brief/preferences/${token}`}
        noindex
      />
      <Panel>
        <div className="ds-stack" style={{ gap: 8 }}>
          <p className="ds-eyebrow ds-eyebrow--teal">{city.name} Referral Brief</p>
          <h1 className="ds-display-2">Email preferences</h1>
        </div>
        {preferences.isLoading ? <p className="ds-small" role="status">Loading your preferences…</p> : null}
        {preferences.isError ? (
          <Notice tone="error">This preferences link has expired. Use the link in your most recent brief.</Notice>
        ) : null}
        {preferences.data ? (
          <form className="ds-stack" style={{ gap: 16 }} onSubmit={submit}>
            <p className="ds-body">Preferences for <strong>{preferences.data.emailMasked}</strong></p>
            <label className="ds-check">
              <input
                type="checkbox"
                checked={practiceOwnerUpdates}
                onChange={(event) => {
                  setPracticeOwnerUpdates(event.target.checked);
                  setState("idle");
                }}
              />
              <span>Also send me the "For practice owners" note (MBS, PSR, running a practice)</span>
            </label>
            {state === "success" ? <Notice tone="success">Preferences saved.</Notice> : null}
            {state === "error" ? <Notice tone="error">Your preferences didn't save. Try again.</Notice> : null}
            <div className="ds-cluster" style={{ justifyContent: "space-between" }}>
              <Button type="submit" disabled={state === "submitting"}>
                {state === "submitting" ? "Saving…" : "Save preferences"}
              </Button>
              <Link className="ds-link ds-small" to={`/brief/unsubscribe/${token}`}>Unsubscribe</Link>
            </div>
          </form>
        ) : null}
      </Panel>
    </FocusShell>
  );
}
